/* istanbul ignore file */
import React, { useMemo, useRef, useState } from 'react';
import Link from 'next/link';

import If from '@components/shared/conditional/If/If';
import useVirtualizedElement from '@hooks/useVirtualizedElement/useVirtualizedElement';
import getResizedImageUrl from '@components/havenly/lib/getResizedImageUrl';
import AspectRatioBox from '@components/shared/AspectRatioBox/AspectRatioBox';
import SaveButton from '@components/shared/buttons/SaveButton/SaveButton';
import ExpandedSaveButton from '@components/shared/buttons/ExpandedSaveButton/ExpandedSaveButton';
import isBrowser from '@utils/browser/isBrowser';
import getBoardName from '@lib/board/getBoardName';
import styles from './DiscoverCard.module.scss';
import {
  CardSize, getAspectRatio, getImgSizes, getImgSrcSet,
} from './util';
import Skeleton from '../Skeleton/Skeleton';

interface IDiscoverBoardItem {
  id: string;
  subStyles?: {
    name: string;
  }[];
  designer?: {
    firstName: string;
    profileUri: string;
  };
  room?: {
    roomType?: {
      type: string;
    };
  };
  mainBoardAsset?: {
    asset?: {
      url: string;
    };
  };
  boardProductCount?: number;
  publicName?: string;
  roomType?: {
    type: string;
  };
}

interface IDiscoverBoardCardProps {
  analyticsListId?: string;
  board: IDiscoverBoardItem;
  saved: boolean;
  onSave: (typename: string, item:any) => void;
  readOnly: boolean;
  size: CardSize;
}

function getBoardAltText(board: IDiscoverBoardItem) {
  return [
    (board.subStyles || []).map((style: any) => style.name).join(', '),
    board.room?.roomType?.type,
    'Design by Havenly Interior Designer',
    board.designer?.firstName || '',
  ]
    .filter((part) => !!part)
    .join(' ')
    .trim();
}

export default function DiscoverBoardCard({
  analyticsListId,
  board,
  saved,
  onSave,
  readOnly,
  size,
}: IDiscoverBoardCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const alt = getBoardAltText(board);
  const imageUrl = board.mainBoardAsset?.asset?.url || '';

  const containerRef = useRef(null);

  const isVisible = !isBrowser() || useVirtualizedElement(containerRef, 50);

  const aspectRatio = getAspectRatio(size);
  const imageDimensions = { x: 400, y: 400 * aspectRatio };
  const imageUrls = useMemo(() => {
    return {
      '400px': (getResizedImageUrl as any)({
        url: imageUrl,
        height: imageDimensions.y,
        width: imageDimensions.x,
        quality: 85,
        fit: null,
      }),
      '800px': (getResizedImageUrl as any)({
        url: imageUrl,
        height: imageDimensions.y * 2,
        width: imageDimensions.x * 2,
        quality: 50,
        fit: null,
      }),
      '1200px': (getResizedImageUrl as any)({
        url: imageUrl,
        height: imageDimensions.y * 3,
        width: imageDimensions.x * 3,
        quality: 50,
        fit: null,
      }),
    };
  }, [imageUrl]);

  const onLoad = () => {
    setImageLoaded(true);
  };

  const onError = () => {
    setImageLoaded(true);
  };

  return (
    // @ts-ignore
    <AspectRatioBox aspectRatio={ aspectRatio } ref={ containerRef }>
      {isVisible && (
        <figure
          className={ styles.Container }
          data-test={ 'board-discover-card' }
          onMouseEnter={ () => setIsHovered(true) }
          onMouseLeave={ () => setIsHovered(false) }
        >
          <BoardImage
            alt={ alt }
            analyticsListId={ analyticsListId }
            imageLoaded={ imageLoaded }
            imageUrls={ imageUrls }
            item={ board }
            onError={ onError }
            onLoad={ onLoad }
            size={ size }
          />
          <div className={ styles.Overlay } />
          <BoardCaption item={ board } size={ size } />
          <If condition={ !readOnly }>
            <If condition={ (size === 'large' || size === 'medium') && isHovered }>
              <ExpandedSaveButton
                className={ styles.SaveButton }
                onClick={ () => onSave('BoardModel', board) }
                saved={ saved }
              />
            </If>
            <If condition={ !(size === 'large' || size === 'medium') || !isHovered }>
              <SaveButton
                className={ `${styles.SaveIconButton} ${saved ? styles.Saved : ''}` }
                hovered={ isHovered }
                onClick={ () => onSave('BoardModel', board) }
                overlay
                saved={ saved }
              />
            </If>
          </If>
        </figure>
      )}
    </AspectRatioBox>
  );
}

function BoardImage(props: any) {
  const {
    analyticsListId,
    item,
  } = props;
  const href = getLink(item, analyticsListId);
  return (
    <Link href={ href }>
      <a href={href} title="View Board">
        <Image { ...props } />
      </a>
    </Link>
  );
}

function Image(props: any) {
  const {
    alt,
    className = '',
    imageLoaded,
    imageUrls,
    onError,
    onLoad,
    size,
  } = props;
  return (
    <>
      <img
        alt={ alt }
        className={ `
          ${className}
          ${styles.Image}
          ${imageLoaded ? styles.isLoaded : ''}
        ` }
        onError={ onError }
        onLoad={ onLoad }
        sizes={ getImgSizes(size) }
        src={ imageUrls['800px'] }
        srcSet={ getImgSrcSet(size, imageUrls) }
      />
      <If condition={ !imageLoaded }>
        <Skeleton radius={2} />
      </If>
    </>
  );
}

function BoardCaption(props: { item: IDiscoverBoardItem; size: CardSize }) {
  const { item, size } = props;
  const havenlyCreated = !item.designer;

  const boardName = getBoardName(
    item?.publicName,
    item?.subStyles,
    item?.roomType?.type,
  );

  return (
    <figcaption className={ styles.OverlayText }>
      <div className={styles.NeverVisible}>{boardName}</div>
      <strong className={ size !== 'small' ? styles.AlwaysVisible : '' }>
        {item.boardProductCount} Product{item.boardProductCount === 1 ? '' : 's'}
      </strong>
      <span className={ size === 'small' ? styles.NeverVisible : '' }>
        &nbsp;&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;&nbsp;Designed by&nbsp;
        <If condition={ havenlyCreated }>
          Havenly
        </If>
        <If condition={ !havenlyCreated }>
          <a
            className={ styles.DesignerLink }
            href={ item.designer!.profileUri }
          >
            {item.designer?.firstName}
          </a>
        </If>
      </span>
    </figcaption>
  );
}

function getLink(item:IDiscoverBoardItem, analyticsListId: string) {
  const analyticsLink = analyticsListId ? `#list=${analyticsListId}` : '';
  return `/interior-design-ideas/design-board/${item.id}${analyticsLink}`;
}
